import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export const Profile: React.FC = () => {
  const auth = useContext(AuthContext);
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [vacationEnabled, setVacationEnabled] = useState(false);

  useEffect(() => {
    // Check if vacation is enabled
    const checkVacationEnabled = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/vacation/public/enabled`
        );
        const data = await response.json();
        if (data.success && data.data.enabled) {
          setVacationEnabled(true);
        }
      } catch (err) {
        console.log('Vacation feature not available');
      }
    };
    checkVacationEnabled();
  }, []);

  const handleLogout = () => {
    auth?.logout();
    navigate('/login');
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
  };

  const getRoleLabel = (role: string) => {
    const roleMap: { [key: string]: string } = {
      'admin': t('administrator'),
      'foreman': t('foreman'),
      'field_worker': t('field_worker')
    };
    return roleMap[role] || role;
  };

  const formatPhoneNumber = (phone: string) => {
    // Format phone as (XXX) XXX-XXXX
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  if (!auth?.user) {
    return null;
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-avatar">
            <span className="avatar-icon">👤</span>
          </div>
          <h1 className="profile-name">
            {auth.user.first_name} {auth.user.last_name}
          </h1>
          {auth.user.nickname && (
            <p className="profile-nickname">"{auth.user.nickname}"</p>
          )}
        </div>

        <div className="profile-section">
          <h2 className="section-title">{t('profile_information')}</h2>

          <div className="profile-field">
            <span className="field-label">{t('email')}</span>
            <span className="field-value">{auth.user.email}</span>
          </div>

          <div className="profile-field">
            <span className="field-label">{t('phone_number')}</span>
            <span className="field-value">{formatPhoneNumber(auth.user.phone)}</span>
          </div>

          <div className="profile-field">
            <span className="field-label">{t('role')}</span>
            <span className="field-value role-badge">
              {getRoleLabel(auth.user.role)}
            </span>
          </div>

          <div className="profile-field">
            <span className="field-label">{t('employee_id')}</span>
            <span className="field-value">#{auth.user.employee_id}</span>
          </div>
        </div>

        <div className="profile-section">
          <h2 className="section-title">{t('preferences')}</h2>

          <div className="profile-field">
            <span className="field-label">{t('language')}</span>
            <button onClick={toggleLanguage} className="language-toggle-btn">
              {language === 'en' ? t('english') : t('spanish')}
              <span className="toggle-icon">🌐</span>
            </button>
          </div>
        </div>

        <div className="profile-actions">
          {vacationEnabled && (
            <button
              onClick={() => navigate('/vacation-time')}
              className="profile-btn vacation-btn"
            >
              🌴 {t('vacation_time')}
            </button>
          )}

          <button
            onClick={handleLogout}
            className="profile-btn logout-btn"
          >
            🚪 {t('logout')}
          </button>
        </div>
      </div>
    </div>
  );
};
